import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { orderBy } from 'lodash';
import ListItem from '@material-ui/core/ListItem';
import classNames from 'classnames';
import css from './styles.module.scss';
import { Button, Typography, Box, Grid, TextField, withStyles } from '@material-ui/core';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { VerticalPopver } from '~/features/dashboard/components/vertical-popover';
import { axiosInstance } from '~/api/config/axios.config';
import { TextFieldOutlined } from '~/common/text-field-outlined';
import { errorHandler } from '~/store/utils/errorHendler';
import { useDispatch, useSelector } from 'react-redux';
import { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete/Autocomplete';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import { NewUser } from '~/store/user/models';
import { selectUserData } from '~/features/dashboard/store/selectors';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import {getContactsThunk, updateIPIListListings} from "~/features/dashboard/store/actions.thunks";
import {useRouteMatch} from "react-router-dom";

export type ItemType = {
    id: number;
    title: string;
};

interface Props {
    title?: string;
    items: ItemType[];
    onSave(item: ItemType, callback?: { (id: number): void }): void;
    onRemove(id: number, callback?: { (id: number): void }): void;
    itemsPerPage?: number;
}

export const EditableListDrawer: React.FC<Props> = ({ title = 'Items', items: rawItems, onSave, onRemove, itemsPerPage = 10 }) => {
    const [editData, setEditData] = React.useState<ItemType | false>(false);

    const sortItems = (itemsToSort: ItemType[]) => orderBy(itemsToSort, [({ title }) => (title.toLowerCase() === 'other' ? 1 : 0), 'title'], ['asc']);
    const [items, setItems] = useState<ItemType[]>(rawItems);

    const save = (item: ItemType) => {
        onSave(item, (id: number): void => {
            if (Number(item.id)) {
                const index = items.findIndex(i => i.id === id);
                if (index > -1) {
                    const tmpItems = [...items];
                    tmpItems.splice(index, 1, item);
                    setItems(tmpItems);
                }
            } else {
                setItems([...items, { id, title: item.title }]);
            }
            setEditData(false);
        });
    };

    const remove = (id: number) => {
        onRemove(id, (id): void => {
            const index = items.findIndex(i => i.id === id);
            if (index > -1) {
                const tmpItems = [...items];
                tmpItems.splice(index, 1);
                setItems(tmpItems);
            }
        });
    };

    const { handleSubmit, register, errors } = useForm<ItemType>();

    if (editData) {
        return (
            <Grid container className={css.module} spacing={3}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" gutterBottom>
                            {editData.id ? 'Edit' : 'Add New'}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box component="form" onSubmit={handleSubmit(save)}>
                        <input type="hidden" name="id" ref={register} defaultValue={editData.id} />
                        <TextFieldOutlined
                            fullWidth
                            inputRef={register({ required: 'Required' })}
                            name="title"
                            variant="filled"
                            label="Title"
                            defaultValue={editData.title}
                            error={!!errors?.title}
                            helperText={errors?.title?.message}
                        />
                        <Box display="flex" mt={4}>
                            <Button variant="contained" color="primary" type="submit" disableElevation style={{ borderRadius: '8px' }}>
                                {editData.id ? 'Update' : 'Create'}
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                disableElevation
                                onClick={() => setEditData(false)}
                                style={{ borderRadius: '8px', marginLeft: '1rem' }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container className={css.module} spacing={3}>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                        Edit {title}
                    </Typography>
                    <Button
                        className={css.btnColorGreen}
                        variant="contained"
                        color="secondary"
                        style={{ height: '2rem', borderRadius: '8px' }}
                        onClick={() => {
                            setEditData({ id: 0, title: '' });
                        }}
                    >
                        Add new
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12}>
                {items.length > 0 && (
                    <Box className={classNames(css.dataList)}>
                        {/*{sortItems(items).slice(0, itemsPerPage).map((i: any) => (*/}
                        {sortItems(items).map((i: any) => (
                            <ListItem key={i.id || i.title.replace(' ', '_')} className={css.item}>
                                <Box width="100%" display="flex" flexDirection="column" justifyContent="center">
                                    <Typography variant="subtitle2">{i.title}</Typography>
                                </Box>
                                {!!i.id && (
                                    <VerticalPopver
                                        childClassName={css.editIcon}
                                        editAction={() => setEditData(i)}
                                        deleteAction={() => {
                                            remove(i.id);
                                        }}
                                    />
                                )}
                            </ListItem>
                        ))}
                        {/* TODO: enable pagination if necessary */}
                        {/*{items.length > itemsPerPage && (*/}
                        {/*    <CollapsibleBlock>*/}
                        {/*        {sortItems(items).slice(itemsPerPage, items.length).map((i: any) => (*/}
                        {/*            <ListItem key={i.id || i.title.replace(' ', '_')} className={css.item}>*/}
                        {/*                <Box width="100%" display="flex" flexDirection="column" justifyContent="center">*/}
                        {/*                    <Typography variant="subtitle2">{i.title}</Typography>*/}
                        {/*                </Box>*/}
                        {/*                {!!i.id && (*/}
                        {/*                    <VerticalPopver*/}
                        {/*                        childClassName={css.editIcon}*/}
                        {/*                        editAction={() => setEditData(i)}*/}
                        {/*                        deleteAction={() => {*/}
                        {/*                            remove(i.id);*/}
                        {/*                        }}*/}
                        {/*                    />*/}
                        {/*                )}*/}
                        {/*            </ListItem>*/}
                        {/*        ))}*/}
                        {/*    </CollapsibleBlock>*/}
                        {/*)}*/}
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

type FormatsProps = Pick<Props, 'items' | 'itemsPerPage' | 'title'> & {
    //
};

export const FormatsListDrawer: React.FC<FormatsProps> = ({ title = 'Formats', ...props }) => {
    const dispatch = useDispatch();

    const onSave = (item: ItemType, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance
            .put('/catalogue/projectCreator/customroyaltyformat/save', {
                id: item.id,
                listingText: item.title,
            })
            .then(({ data }) => {
                callback && callback(data.id);
            })
            .catch(e => {
                if (e?.response?.data?.ExceptionMessage) {
                    errorHandler(e?.response?.data?.ExceptionMessage, dispatch);
                } else {
                    throw e;
                }
            });
    };

    const onRemove = (id: number, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance.delete(`/catalogue/projectCreator/customroyaltyformat/delete/${id}`).then(({ data }) => {
            if (data) {
                callback && callback(id);
            } else {
                errorHandler('Something went wrong', dispatch);
            }
        });
    };

    return <EditableListDrawer title={title} {...props} onSave={onSave} onRemove={onRemove} />;
};

type CodesProps = Pick<Props, 'items' | 'itemsPerPage' | 'title'> & {
    //
};

export const CodesListDrawer: React.FC<CodesProps> = ({ title = 'Codes', ...props }) => {
    const dispatch = useDispatch();

    const onSave = (item: ItemType, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance
            .put('/catalogue/projectCreator/customanalysiscode/save', {
                id: item.id,
                listingText: item.title,
            })
            .then(({ data }) => {
                callback && callback(data.id);
            })
            .catch(e => {
                if (e?.response?.data?.ExceptionMessage) {
                    errorHandler(e?.response?.data?.ExceptionMessage, dispatch);
                } else {
                    throw e;
                }
            });
    };

    const onRemove = (id: number, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance.delete(`/catalogue/projectCreator/customanalysiscode/delete/${id}`).then(({ data }) => {
            if (data) {
                callback && callback(id);
            } else {
                errorHandler('Something went wrong', dispatch);
            }
        });
    };

    return <EditableListDrawer title={title} {...props} onSave={onSave} onRemove={onRemove} />;
};

type RegionsProps = Pick<Props, 'items' | 'itemsPerPage' | 'title'> & {
    //
};

export const RegionsListDrawer: React.FC<RegionsProps> = ({ title = 'Regions', ...props }) => {
    const dispatch = useDispatch();

    const onSave = (item: ItemType, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance
            .put('/catalogue/projectCreator/customregion/save', {
                id: item.id,
                listingText: item.title,
            })
            .then(({ data }) => {
                callback && callback(data.id);
            })
            .catch(e => {
                if (e?.response?.data?.ExceptionMessage) {
                    errorHandler(e?.response?.data?.ExceptionMessage, dispatch);
                } else {
                    throw e;
                }
            });
    };

    const onRemove = (id: number, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance.delete(`/catalogue/projectCreator/customregion/delete/${id}`).then(({ data }) => {
            if (data) {
                callback && callback(id);
            } else {
                errorHandler('Something went wrong', dispatch);
            }
        });
    };

    return <EditableListDrawer title={title} {...props} onSave={onSave} onRemove={onRemove} />;
};

export const SocietyListDrawer: React.FC<{ title: string; closeDrawer: () => void }> = ({ title = 'Society', closeDrawer }) => {
    const user = useSelector(selectUserData);
    const match = useRouteMatch<{ creatorId: string }>();
    const creatorId = useMemo(() => Number(match.params.creatorId), [match.params.creatorId]);
    const [kontactCode, setKontactCode] = useState('');
    const dispatch = useThunkDispatch();
    const [piSociety, setPiSociety] = useState<{ affiliation: string }[]>([
        {
            affiliation: 'aff',
        },
    ]);

    useEffect(() => {
        user && setKontactCode(user.contactCode);
        user?.ipiList.length === 0 && ipiListAppend({ ...initialSocietyData });
        console.log(ipiListFields);
        //eslint-disable-next-line
    }, [user]);

    useEffect(() => {
        axiosInstance.get('Catalogue/projectReference/ipisociety/list').then(data => {
            let data1 = data.data.filter((item: any) => {
                if (
                    item.affiliation === '' ||
                    item.affiliation == null ||
                    item.affiliation === undefined ||
                    item.definition === '' ||
                    item.definition == null ||
                    item.definition === undefined
                ) {
                    return false;
                } else {
                    return item;
                }
            });
            let piSocietyArray: any[] = [];
            data1.map((item: any) => piSocietyArray.push(item.affiliation));
            setPiSociety(piSocietyArray);
        });
    }, []);

    let initialSocietyData = {
        kontacts_IPI_Id: 0,
        iPNumber: '',
        affiliation: '',
        kontactCode: kontactCode,
    };

    const { handleSubmit, errors, control } = useForm<NewUser>({
        defaultValues: {
            ipiList: user?.ipiList || [{ ...initialSocietyData }],
        },
    });

    const deletesociety = async (i: any, values: any) => {
        ipiListRemove(i);
        await axiosInstance.delete(`/Contract/kontacts_IPI/delete/${values.kontacts_IPI_Id}`).then(async () => {
            await dispatch(getContactsThunk({ creatorId }));
        });
    };

    const { fields: ipiListFields, append: ipiListAppend, remove: ipiListRemove } = useFieldArray({ control, name: 'ipiList' });

    const CustomAutocomplete = withStyles({
        inputRoot: {
            '&[class*="MuiFilledInput-root"]': {
                paddingTop: 0,
                paddingLeft: 0,
            },
        },
    })(Autocomplete);

    function isNumberKey(evt: any) {
        let charCode = evt.which ? evt.which : evt.keyCode;
        return !(charCode > 31 && charCode !== 46 && (charCode < 48 || charCode > 57));
    }

    const onSubmit = useCallback(
        (data: { ipiList: any[] }) => {
            dispatch(updateIPIListListings({ ...data, creatorId })).then(() => {
                closeDrawer();
                dispatch(getContactsThunk({ creatorId }));
            });
        },
        // eslint-disable-next-line
        [dispatch]
    );

    return (
        <div style={{ padding: '30px' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div style={{ display: 'flex' }}>
                    <Typography style={{ width: '40%' }} className={classNames(css.bold, css.infoTitle)}>
                        Society
                    </Typography>
                    <Typography className={classNames(css.bold, css.infoTitle)}>IPI /CAE number</Typography>
                </div>
                {ipiListFields.map((item, indexno) => (
                    <div key={item.id} style={{ display: 'flex', position: 'relative' }}>
                        <Controller
                            render={props => (
                                <CustomAutocomplete
                                    {...props}
                                    options={piSociety}
                                    className={css.input}
                                    fullWidth
                                    style={{ width: '40%' }}
                                    renderInput={(p: AutocompleteRenderInputParams) => (
                                        <>
                                            <TextField
                                                className={classNames(css.textInput, css.textSociety)}
                                                {...p}
                                                variant="filled"
                                                value={props.value}
                                                defaultValue={props.value}
                                                error={!!errors.affiliation}
                                                helperText={errors.affiliation ? errors.affiliation.message : ''}
                                            />
                                        </>
                                    )}
                                    onChange={(_, data) => props.onChange(data)}
                                />
                            )}
                            name={`ipiList[${indexno}].affiliation`}
                            control={control}
                        />
                        <Controller
                            render={props => <TextField fullWidth hidden value={props.value} onChange={data => props.onChange(data)} />}
                            defaultValue={item.kontactCode || kontactCode}
                            name={`ipiList[${indexno}].kontactCode`}
                            control={control}
                        />

                        <Controller
                            render={props => (
                                <TextField fullWidth hidden value={props.value} label={'kontacts_IPI_Id'} onChange={data => props.onChange(data)} />
                            )}
                            defaultValue={item.kontacts_IPI_Id || 0}
                            name={`ipiList[${indexno}].kontacts_IPI_Id`}
                            control={control}
                        />

                        <Controller
                            render={props => (
                                <TextField
                                    fullWidth
                                    type={'number'}
                                    onKeyPress={isNumberKey}
                                    InputProps={{ inputProps: { min: '0', max: '99999999999' } }}
                                    value={props.value}
                                    label={''}
                                    className={css.textInput}
                                    onChange={data => props.onChange(data)}
                                    style={{ width: '70%', marginLeft: '15px' }}
                                />
                            )}
                            defaultValue={item.iPNumber}
                            name={`ipiList[${indexno}].iPNumber`}
                            control={control}
                        />
                        {indexno === 0 ? (
                            <AddCircleOutlineIcon className={css.addButton} onClick={() => ipiListAppend({ ...initialSocietyData })} />
                        ) : (
                            <CloseIcon className={css.deleteSociety} onClick={() => deletesociety(indexno, item)} />
                        )}
                    </div>
                ))}
                <Button className={css.btnSubmit} type={'submit'}>
                    Submit
                </Button>
            </form>
        </div>
    );
};